import React from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { sanitizeUrl } from "../../utils/urlSanitizer";
import { parseScrollToArticleId } from "../../utils/navigateToInternalArticle";
import { findMatchRanges, parseSearchQuery } from "./chatSearchIndex";
import "./ChatMessageMarkdown.css";

function goToInternalArticle(articleId) {
  const id = String(articleId);
  window.location.href = `/articles?scrollToArticle=${encodeURIComponent(id)}&internal=1`;
}

const MAX_LENGTH = 800;

/** Module level on purpose: react-markdown keys its output off these functions,
 *  so rebuilding the object per render makes React remount the whole message
 *  instead of updating it -- which silently drops any text the reader has
 *  selected. Nothing in here depends on props, so it can stay a constant. */
const markdownComponents = {
  a: ({ href, children }) => {
    const safeHref = sanitizeUrl(href);
    const articleId =
      parseScrollToArticleId(safeHref) ||
      parseScrollToArticleId(href || "") ||
      parseScrollToArticleId(typeof children === "string" ? children : "");

    if (articleId) {
      // Use a <span> (not <a>) so chat UI / nested routers cannot swallow
      // native anchor navigation. Window interceptor also listens for
      // data-scroll-to-article.
      return (
        <span
          role="link"
          tabIndex={0}
          data-scroll-to-article={articleId}
          className="internal-article-chat-link chat-message-markdown__link"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToInternalArticle(articleId);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToInternalArticle(articleId);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              goToInternalArticle(articleId);
            }
          }}
        >
          {children}
        </span>
      );
    }

    return (
      <a href={safeHref} target="_blank" rel="noopener noreferrer" className="chat-message-markdown__link">
        {children}
      </a>
    );
  },
  h1: ({ children }) => <div className="h3-bold-markdown markdown-header-h1-h4 markdown-header-colored">{children}</div>,
  h2: ({ children }) => <div className="h3-bold-markdown markdown-header-h1-h4 markdown-header-colored">{children}</div>,
  h3: ({ children }) => <div className="h3-bold-markdown markdown-header-h1-h4 markdown-header-colored">{children}</div>,
  h4: ({ children }) => <div className="h3-bold-markdown markdown-header-h1-h4 markdown-header-colored">{children}</div>,
  h5: ({ children }) => <div className="h3-bold-markdown markdown-header-h5-h6">{children}</div>,
  h6: ({ children }) => <div className="h3-bold-markdown markdown-header-h5-h6">{children}</div>,
  ul: ({ children }) => <ul className="markdown-ul-small">{children}</ul>,
  ol: ({ children }) => <ol className="markdown-ul-small">{children}</ol>,
  p: ({ children }) => {
    const empty =
      children == null ||
      children === "" ||
      (Array.isArray(children) && children.filter(Boolean).length === 0);
    return <p>{empty ? <br /> : children}</p>;
  },
  script: () => null,
  iframe: () => null,
};

const REMARK_PLUGINS = [remarkGfm, remarkBreaks];
const REHYPE_PLUGINS = [rehypeRaw];

/**
 * rehype plugin: wraps every case-insensitive occurrence of `term` in the
 * text nodes in <mark class="chat-search-mark"> (in-chat search, mobile: the
 * matched words stay highlighted in the bubble).
 */
export const markSearchTerms = (terms, wordStartOnly) => () => (tree) => {
  if (!terms || !terms.length) return;
  const visit = (node) => {
    if (!node || !Array.isArray(node.children)) return;
    if (node.type === "element" && (node.tagName === "mark" || node.tagName === "script" || node.tagName === "style")) return;
    const next = [];
    node.children.forEach((child) => {
      if (child.type !== "text" || typeof child.value !== "string") {
        visit(child);
        next.push(child);
        return;
      }
      const value = child.value;
      const ranges = findMatchRanges(value, terms, wordStartOnly);
      if (!ranges.length) {
        next.push(child);
        return;
      }
      let from = 0;
      ranges.forEach(([start, end]) => {
        if (start > from) next.push({ type: "text", value: value.slice(from, start) });
        next.push({
          type: "element",
          tagName: "mark",
          properties: { className: ["chat-search-mark"] },
          children: [{ type: "text", value: value.slice(start, end) }],
        });
        from = end;
      });
      if (from < value.length) next.push({ type: "text", value: value.slice(from) });
    });
    node.children = next;
  };
  visit(tree);
};

const ChatMessageMarkdown = React.memo(({
  message,
  messageId,
  isExpanded,
  toggleMessageExpansion,
  isDesktop,
  screenWidth,
  moreLabel = "...Mehr",
  highlight = "",
}) => {
  // highlight: a query string, or { terms, wordStartOnly } (ranked in-chat search results)
  const terms = highlight && typeof highlight === "object"
    ? (Array.isArray(highlight.terms) ? highlight.terms : [])
    : parseSearchQuery(highlight);
  const wordStartOnly = !!(highlight && typeof highlight === "object" && highlight.wordStartOnly);
  const term = terms.join(" ");
  const rehypePlugins = React.useMemo(
    () => (term ? [rehypeRaw, markSearchTerms(term.split(" "), wordStartOnly)] : REHYPE_PLUGINS),
    [term, wordStartOnly]
  );
  if (!message) return null;

  const needsTruncation = messageId && message.length > MAX_LENGTH;
  const displayMessage = (needsTruncation && !isExpanded && !term) ? message.substring(0, MAX_LENGTH) : message;

  return (
    <div className={`chat-md-body${isDesktop ? " chat-message-markdown--desktop" : ""}`}>
      <Markdown
        rehypePlugins={rehypePlugins}
        remarkPlugins={REMARK_PLUGINS}
        skipHtml={false}
        components={markdownComponents}
      >
        {displayMessage}
      </Markdown>
      {needsTruncation && !isExpanded && !term && (
        <span className="mehr-button" onClick={() => toggleMessageExpansion(messageId)}>
          {moreLabel}
        </span>
      )}
    </div>
  );
});

ChatMessageMarkdown.displayName = "ChatMessageMarkdown";

export default ChatMessageMarkdown;
